/**
 * salvia-861: guarda os comandos ditos na consulta fora da memória do componente.
 *
 * POR QUE EXISTE. O `AppointmentDialog` é montado com `v-if`: recarregar a página
 * no meio da consulta apagava tudo que o profissional tinha ditado. Ele falou,
 * viu o chip aparecer, confiou — e perdeu, sem aviso. A guarda de fechamento
 * cobre a saída pela porta; não cobre o F5.
 *
 * POR QUE localStorage E NÃO TABELA. A consulta acontece numa sessão, num
 * aparelho, e o modo de falha real é o refresh. Uma tabela sobreviveria a trocar
 * de máquina — o que não é o que acontece no meio de um atendimento — e custaria
 * migration, endpoint e limpeza. Se um dia o atendimento virar multi-dispositivo,
 * esta é a peça a trocar, e o resto do código não muda: a interface é load/save/
 * clear por paciente.
 *
 * ⚠️ A CHAVE É POR PACIENTE, e isso não é detalhe de organização. Comando ditado
 * na consulta de uma paciente aparecendo na de outra não é inconveniência: é dado
 * clínico no prontuário errado. Por isso nada é lido sem o `patientId`, e uma
 * chave sem paciente não existe.
 */

const PREFIXO = "salvia.comandosDeVoz";

/**
 * Comandos mais velhos que isto são descartados na leitura.
 *
 * Sem validade, um comando ditado numa consulta abandonada há duas semanas
 * ressurgiria no próximo atendimento daquela paciente, sem contexto nenhum — e o
 * profissional aprovaria uma proposta sobre uma conversa que não lembra.
 */
const VALIDADE_MS = 12 * 60 * 60 * 1000; // 12h: cobre o dia de trabalho, não mais.

function chave(patientId) {
  return `${PREFIXO}.${patientId}`;
}

function disponivel() {
  try {
    return typeof window !== "undefined" && !!window.localStorage;
  } catch (e) {
    // Safari em modo privado lança ao tocar no localStorage. Perder a persistência
    // é ruim; quebrar a consulta por causa dela seria pior.
    return false;
  }
}

/**
 * ⚠️ O ATENDIMENTO também entra na conta, não só o paciente — 22/09.
 *
 * A chave é por paciente e a validade é de 12h, o que cobre o refresh. Mas duas
 * consultas do MESMO paciente dentro da janela compartilhavam o registro: em
 * test, abrir um atendimento novo trouxe três comandos ditados no anterior. O
 * profissional via comandos que ele não deu naquela consulta, e a varredura do
 * fim os trataria como se fossem dela.
 *
 * Por isso o `appointmentId` é guardado junto e conferido na leitura. Ele não
 * entra na CHAVE porque pode não existir ainda (o diálogo abre antes de a
 * consulta ser criada); usá-lo na chave perderia o que foi ditado nesse intervalo.
 *
 * A regra do descarte é deliberadamente conservadora: só descarta quando os dois
 * ids existem e diferem. Se o guardado não tem id, ele pertence a um atendimento
 * que ainda não tinha sido salvo — provavelmente ESTE — e jogar fora seria a
 * perda silenciosa que este arquivo inteiro existe para evitar.
 *
 * @param {number|string} patientId
 * @param {number|string} [appointmentId] O atendimento aberto agora.
 * @returns {{comando: string, em: string}[]} vazio se não houver, se expirou, se
 *          era de outro atendimento, ou se o armazenamento não estiver disponível.
 */
export function carregarComandos(patientId, appointmentId) {
  if (!patientId || !disponivel()) return [];
  try {
    const bruto = window.localStorage.getItem(chave(patientId));
    if (!bruto) return [];

    const guardado = JSON.parse(bruto);
    const idade = Date.now() - (guardado.em || 0);
    if (idade > VALIDADE_MS) {
      limparComandos(patientId);
      return [];
    }

    const doOutro =
      guardado.appointmentId != null &&
      appointmentId != null &&
      String(guardado.appointmentId) !== String(appointmentId);
    if (doOutro) {
      limparComandos(patientId);
      return [];
    }

    return Array.isArray(guardado.comandos) ? guardado.comandos : [];
  } catch (e) {
    // JSON corrompido não pode travar a tela de atendimento.
    return [];
  }
}

export function salvarComandos(patientId, comandos, appointmentId) {
  if (!patientId || !disponivel()) return;
  try {
    if (!comandos || !comandos.length) {
      limparComandos(patientId);
      return;
    }
    window.localStorage.setItem(
      chave(patientId),
      JSON.stringify({
        em: Date.now(),
        appointmentId: appointmentId ?? null,
        comandos,
      }),
    );
  } catch (e) {
    // Cota estourada, por exemplo. Segue sem persistir.
  }
}

export function limparComandos(patientId) {
  if (!patientId || !disponivel()) return;
  try {
    window.localStorage.removeItem(chave(patientId));
  } catch (e) {
    /* nada a fazer */
  }
}
